'use client';

import { useCallback, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { RecentDocuments } from '@/components/datahunter/recent-documents';
import { CollabPanel } from '@/components/datahunter/collab-panel';
import { useToolDocument } from '@/components/datahunter/use-tool-document';
import { useCollab } from '@/components/datahunter/use-collab';
import type { DocumentKind } from '@/lib/tools-storage';

/**
 * Каркас редактора: история слева, документ справа, совместная работа сверху.
 *
 * Общий для JSON и Markdown, потому что различаются они только панелью
 * действий и тем, что показано рядом с текстом. Всё остальное — хранение,
 * список недавних, совместная сессия — у них одинаковое.
 *
 * Само поле ввода передаётся снаружи: у Markdown рядом с ним живёт
 * предпросмотр, у JSON — сообщение об ошибке.
 */
export function EditorShell({
  kind,
  title,
  toolbar,
  children,
}: {
  kind: DocumentKind;
  title: string;
  /** Кнопки, специфичные для инструмента: им нужен текст документа. */
  toolbar?: (props: {
    value: string;
    onChange: (value: string) => void;
    readOnly: boolean;
  }) => React.ReactNode;
  /** Область редактирования: получает текст и обработчик изменения. */
  children: (props: {
    value: string;
    onChange: (value: string) => void;
    readOnly: boolean;
  }) => React.ReactNode;
}) {
  const t = useTranslations('dh_editor');
  const doc = useToolDocument(kind, t('untitled'));
  const [roomId, setRoomId] = useState<string | null>(null);

  const collab = useCollab({
    roomId,
    kind,
    content: doc.content,
    onRemoteContent: doc.setContent,
  });

  // Переход по ссылке-приглашению: комната берётся из адреса.
  useEffect(() => {
    const invited = new URLSearchParams(window.location.search).get('room');
    if (invited) setRoomId(invited);
  }, []);

  const share = useCallback(async () => {
    const id = await collab.share(doc.id, doc.content);
    if (id) setRoomId(id);
  }, [collab, doc.id, doc.content]);

  // В комнате без права правки поле только для чтения. Вне комнаты человек
  // работает со своим документом и никаких ограничений быть не должно.
  const readOnly = roomId !== null && !collab.canEdit;

  return (
    <div className="max-w-[1280px] mx-auto px-6 py-10">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold">{title}</h1>
        <div className="flex flex-wrap items-center gap-2">
          {toolbar?.({ value: doc.content, onChange: doc.setContent, readOnly })}
          {!roomId && (
            <CollabPanel
              roomId={null}
              connected={false}
              isOwner={false}
              canEdit
              openEditing={false}
              members={[]}
              me={collab.me}
              onShare={share}
              onToggleOpenEditing={() => {
                /* до создания комнаты управлять нечем */
              }}
              onMemberAccess={() => {
                /* см. выше */
              }}
            />
          )}
          <span className="text-xs text-[var(--color-text-muted)]">
            {doc.saving ? t('saving') : t('saved')}
          </span>
        </div>
      </div>

      {roomId && (
        <div className="mb-6">
          <CollabPanel
            roomId={roomId}
            connected={collab.connected}
            isOwner={collab.isOwner}
            canEdit={collab.canEdit}
            openEditing={collab.openEditing}
            members={collab.members}
            me={collab.me}
            onShare={share}
            onToggleOpenEditing={(value) => {
              void collab.setOpenEditing(value);
            }}
            onMemberAccess={(member, allow) => {
              void collab.setMemberAccess(member, allow);
            }}
          />
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-8">
        <RecentDocuments
          items={doc.recent}
          activeId={doc.id}
          onOpen={doc.open}
          onCreate={doc.create}
          onRemove={(id) => {
            void doc.remove(id);
          }}
        />

        <div className="flex-1 min-w-0">
          {children({ value: doc.content, onChange: doc.setContent, readOnly })}
        </div>
      </div>
    </div>
  );
}
